import { useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthLayout } from '@/layouts/AuthLayout';
import {
  clearSession,
  getPartialToken,
  getSessionEmail,
  isTokenExpired,
  startSession,
} from '@/lib/session';

import styles from './LoginPage.module.scss';
import { platformVerifyMfa } from '../api/platform-auth.api';

export function MfaPage(): JSX.Element {
  const navigate = useNavigate();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = useRecovery ? recoveryCode.trim().length > 0 : code.length === 6;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setError('');

    const partialToken = getPartialToken() ?? '';
    if (!partialToken) {
      void navigate('/login');
      return;
    }
    // OS-1710 — el token parcial dura 5 min: si venció, volver al login con el motivo.
    if (isTokenExpired(partialToken)) {
      clearSession();
      void navigate('/login?expired=1');
      return;
    }

    try {
      const { accessToken } = await platformVerifyMfa(
        useRecovery ? { recoveryCode: recoveryCode.trim() } : { code },
        partialToken,
      );
      startSession(accessToken, getSessionEmail());
      void navigate('/dashboard');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  function toggleMode(): void {
    setUseRecovery((current) => !current);
    setError('');
  }

  return (
    <AuthLayout
      title="Verificación MFA"
      subtitle={
        useRecovery ? 'Ingresa uno de tus códigos de recuperación' : 'Ingresa el código de tu autenticador'
      }
    >
      <form className={styles.form} onSubmit={(e) => void handleSubmit(e)}>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        {useRecovery ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="recoveryCode">
              Código de recuperación
            </label>
            <input
              id="recoveryCode"
              type="text"
              className={styles.input}
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              placeholder="XXXX-XXXX-XXXX"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              required
              autoFocus
            />
            <p className={styles.hint}>Cada código sirve una sola vez.</p>
          </div>
        ) : (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="code">
              Código TOTP (6 dígitos)
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              className={styles.input}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              required
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
        )}

        <button type="submit" className={styles.submitBtn} disabled={loading || !canSubmit}>
          {loading ? 'Verificando…' : 'Acceder'}
        </button>

        <button type="button" className={styles.linkBtn} onClick={toggleMode}>
          {useRecovery ? 'Usar el código del autenticador' : 'No tengo mi autenticador: usar un código de recuperación'}
        </button>
      </form>
    </AuthLayout>
  );
}
